import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getBudgets, getBudgetById, createBudget, updateBudget, deleteBudgetById, setBudgetIsActive, triggerPublishBudgets } from "../services/budget-api";
import { Budget, NewBudget, BudgetHead } from "../types/budget-api";

export const useBudget = (clientId: string, connectionId: string) => {
  const queryClient = useQueryClient();

  const { data: budgets, isLoading: isLoadingBudgets, error: budgetsError } = useQuery({
    queryKey: ["budgets", clientId, connectionId],
    queryFn: () => getBudgets(clientId, connectionId),
    enabled: Boolean(clientId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  const { mutate: createNewBudget, isPending: isCreating, isSuccess: createSuccess, error: createError } = useMutation({
    mutationFn: (newBudget: NewBudget) => createBudget(newBudget),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgets", clientId] });
    },
  });

  const { mutate: updateExistingBudget, isPending: isUpdating, isSuccess: updateSuccess, error: updateError } = useMutation({
    mutationFn: ({ id, budget }: { id: string; budget: Partial<Budget> & BudgetHead }) =>
      updateBudget(id, budget as Budget),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["budgets", clientId] });
      queryClient.invalidateQueries({ queryKey: ["budget", variables.id] });
    },
  });

  const { mutateAsync: deleteBudget, isPending: isDeleting, isSuccess: deleteSuccess, error: deleteError } = useMutation({
    mutationFn: (id: string) => deleteBudgetById(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgets", clientId] });
    },
  });

  const getBudget = (id: string | null) => {
    return useQuery({
      queryKey: ["budget", id],
      queryFn: () => getBudgetById(id!),
      enabled: Boolean(id),
      staleTime: 0, // Always consider data stale
      gcTime: 0, // Don't cache the data
      retry: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });
  };

  const { mutate: updateBudgetName, isPending: isUpdatingName, isSuccess: updateNameSuccess, error: updateNameError } = useMutation({
    mutationFn: ({ id, newName, budget }: { id: string; newName: string, budget: Partial<Budget> & BudgetHead }) => {
      console.log(newName);
      budget.name = newName;
      console.log(budget);
      return updateBudget(id, budget as Budget);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["budgets", clientId, connectionId] });
      queryClient.invalidateQueries({ queryKey: ["budget", variables.id] });
    },
    onError: (error) => {
      console.log(error);
    }
  });

  const { mutate: setBudgetActive, isPending: isSettingActive, isSuccess: setActiveSuccess, error: setActiveError } = useMutation({
    mutationFn: ({ clientId, connectionId, budgetId }: { clientId: string; connectionId: string; budgetId: string }) =>
      setBudgetIsActive(clientId, connectionId, budgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgets", clientId, connectionId] }); // Refresh budgets after setting active
    },
  });

  const { mutate: publishBudgets, isPending: isPublishing, isSuccess: publishSuccess, error: publishError } = useMutation({
    mutationFn: ({ clientId, connectionId }: { clientId: string; connectionId: string }) =>
      triggerPublishBudgets(clientId, connectionId),
     onSuccess: () => {
       queryClient.invalidateQueries({ queryKey: ["budgets", clientId, connectionId] }); // Refresh budgets after setting active
    },
  });

  return {
    budgets,
    isLoadingBudgets,
    budgetsError,
    createNewBudget,
    isCreating,
    createSuccess,
    createError,
    updateExistingBudget,
    isUpdating,
    updateSuccess,
    updateError,
    getBudget,
    deleteBudget,
    isDeleting,
    deleteSuccess,
    deleteError,
    updateBudgetName,
    isUpdatingName,
    updateNameSuccess,
    updateNameError,
    setBudgetActive,
    isSettingActive,
    setActiveSuccess,
    setActiveError,
    publishBudgets,
    isPublishing,
    publishSuccess,
    publishError
  };
};